import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { BsCheck2, BsArrowRight } from 'react-icons/bs';
import { AppWrap } from '../../wrapper';
import { client } from '../../client';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import {
	BASE_CURRENCY,
	CURRENCIES,
	CURRENCY_CODES,
	DEFAULT_CURRENCY,
	convert,
	formatPrice,
	formatRate,
	formatRateDate,
	parsePrice,
	roundPrice,
} from '../../utils/currency';
import './Services.scss';

const CURRENCY_STORAGE_KEY = 'preferred-currency';

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const gridStagger = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.12, delayChildren: 0.08 } },
};

const cardReveal = {
	hidden: { y: 60, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const readStoredCurrency = () => {
	try {
		const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
		return CURRENCY_CODES.includes(saved) ? saved : DEFAULT_CURRENCY;
	} catch {
		return DEFAULT_CURRENCY;
	}
};

const ServicePrice = ({ pricing, currency, rates }) => {
	const parsed = parsePrice(pricing);
	const converted = parsed && convert(parsed.amount, parsed.currency, currency, rates);

	// Copy like "Custom quote", or a currency we can't reach a rate for.
	if (!parsed || converted === null) {
		return (
			<div className='service-price'>
				<span className='service-price-value'>{pricing}</span>
			</div>
		);
	}

	// Tidying only makes sense for a computed number — an authored price is exact.
	const isConverted = parsed.currency !== currency;
	const formatted = formatPrice(isConverted ? roundPrice(converted, currency) : converted, currency);

	return (
		<div className='service-price'>
			{isConverted && (
				<span className='service-price-approx' role='img' aria-label='approximately'>
					≈
				</span>
			)}
			<motion.span
				className='service-price-value'
				key={formatted}
				initial={{ opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
			>
				{formatted}
			</motion.span>
		</div>
	);
};

const Services = () => {
	const [services, setServices] = useState([]);
	const [currency, setCurrency] = useState(readStoredCurrency);
	const { rates, updatedAt, status } = useExchangeRates();

	useEffect(() => {
		let mounted = true;
		client.fetch('*[_type == "services"]').then((data) => {
			if (mounted) setServices(data || []);
		});
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		try {
			localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
		} catch {
			/* a remembered preference is a nicety, not a requirement */
		}
	}, [currency]);

	const sortedServices = useMemo(
		() => [...services].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
		[services]
	);

	const rateNote = useMemo(() => {
		if (status === 'loading') return 'Fetching live exchange rates…';
		if (status === 'fallback') return 'Live rates unavailable — prices are approximate.';
		if (currency === BASE_CURRENCY) return `Billed in ${BASE_CURRENCY} — no conversion applied.`;

		const rate = formatRate(currency, rates);
		const date = formatRateDate(updatedAt);

		return [rate, date && `updated ${date}`].filter(Boolean).join(' · ');
	}, [status, currency, rates, updatedAt]);

	return (
		<div className='services-content'>
			<motion.div
				className='services-head'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<div>
					<p className='eyebrow'>What I Offer</p>
					<h2 className='services-heading'>
						Web <span className='text-gradient'>services</span>
						<br />& <em>pricing</em>
					</h2>
				</div>
				<p className='services-intro'>
					Transparent packages built around what your project actually needs — pick a
					tier, or reach out for something tailored.
				</p>

				<div className='fx-switch'>
					<span className='fx-switch-label' id='fx-switch-label'>
						Prices shown in
					</span>

					<div className='fx-switch-track' role='radiogroup' aria-labelledby='fx-switch-label'>
						{CURRENCIES.map(({ code, name }) => (
							<label
								key={code}
								className={`fx-option ${code === currency ? 'is-active' : ''}`}
								title={name}
							>
								<input
									type='radio'
									name='currency'
									value={code}
									aria-label={name}
									checked={code === currency}
									onChange={() => setCurrency(code)}
								/>
								{code === currency && (
									<motion.span
										className='fx-option-pill'
										layoutId='fx-option-pill'
										transition={{ type: 'spring', stiffness: 420, damping: 36 }}
									/>
								)}
								<span className='fx-option-code'>{code}</span>
							</label>
						))}
					</div>

					<p className={`fx-status is-${status}`} aria-live='polite'>
						<span className='fx-status-dot' aria-hidden='true' />
						{rateNote}
					</p>
				</div>
			</motion.div>

			{/*
			  * Mounted only once the cards exist. `whileInView` + `once` on an empty grid
			  * fires the observer and disconnects it, and cards that mount afterwards never
			  * receive the variant change — they'd stay stuck on `hidden`.
			  */}
			{sortedServices.length > 0 && (
				<motion.div
					className='services-grid'
					variants={gridStagger}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.15 }}
				>
					{sortedServices.map((service, index) => {
						const featured = Boolean(service.popularityBadge);
						return (
							<motion.article
								className={`service-card ${featured ? 'featured' : ''}`}
								variants={cardReveal}
								key={`${service.title}-${index}`}
							>
								{featured && (
									<span className='service-badge'>{service.popularityBadge}</span>
								)}

								<div className='service-card-head'>
									<h3 className='service-title'>{service.title}</h3>
									{service.maxpages && (
										<span className='service-meta'>{service.maxpages}</span>
									)}
								</div>

								{service.pricing && (
									<ServicePrice
										pricing={service.pricing}
										currency={currency}
										rates={rates}
									/>
								)}

								<div className='service-features'>
									{(service.content || []).map((block, i) => (
										<PortableText key={i} value={block} />
									))}
								</div>

								<a
									href='#contact'
									className={`btn ${featured ? 'btn-gradient' : 'btn-ghost'} service-cta`}
								>
									Start a project
								</a>
							</motion.article>
						);
					})}
				</motion.div>
			)}

			<motion.aside
				className='services-cta'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<div className='services-cta-glow' aria-hidden='true' />

				<div className='services-cta-text'>
					<p className='eyebrow'>Still deciding?</p>
					<h3 className='services-cta-title'>
						Let's build something <span className='text-gradient'>worth talking about</span>
					</h3>
					<p className='services-cta-sub'>
						Not sure which package fits? Every build comes with revisions until it's
						right, optional fast-track delivery, and flexible pricing for custom scopes
						or long-term work. Tell me about your project — I'll tailor the rest.
					</p>

					<ul className='services-cta-points'>
						<li>
							<BsCheck2 aria-hidden='true' /> Revisions included
						</li>
						<li>
							<BsCheck2 aria-hidden='true' /> Fast-track delivery
						</li>
						<li>
							<BsCheck2 aria-hidden='true' /> Flexible pricing
						</li>
					</ul>
				</div>

				<div className='services-cta-action'>
					<a href='#contact' className='btn btn-gradient services-cta-btn'>
						Get a free quote <BsArrowRight aria-hidden='true' />
					</a>
					<span className='services-cta-microcopy'>
						Free consultation · No commitment
					</span>
				</div>
			</motion.aside>
		</div>
	);
};

export default AppWrap(Services, 'services');
